const CART_MESSAGES = {
  select: () => 'Please Select Size',
  out_of_stock: () => 'Out of Stock'
}

/** Replaces the price in the CTA's right-hand slot when there is nothing to buy at that size. */
const CTA_PRICE_MESSAGES = {
  notify: () => 'Notify Me'
}

const MESSAGE_TYPES = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
}
/**
 * Product form
 */
class ProductForm {
  static PRODUCT_DETAILS_SELECTOR = '[data-role="product_id"]'
  static FORM_SELECTOR = '#product-form'
  static FORM_SUBMIT_SELECTOR = '[data-form="product-form"]'
  static FORM_SUBMIT_MESSAGE_SELECTOR = '[data-role="cta_label"]'
  static NOTIFY_SELECTOR = '[data-role="klaviyo_notify_me"]'
  static PRODUCT_SELECT_SELECTOR = 'x-product-selector'
  static NOTIFY_INPUT_SELECTOR = '[data-role="klaviyo_input"]'
  static NOTIFY_SUCCESS_MESSAGE_SELECTOR = '#BackInStockSuccess'
  static NOTIFY_ERROR_MESSAGE_SELECTOR = '#BackInStockError'
  static PRE_RELEASE_SELECTOR = '[data-role="pdp-pre-release"]'
  static WISHLIST_BUTTON_SELECTOR = 'x-storage'
  static IS_GIFT_CARD_SELECTOR = 'input[name="pdp_type"]'
  static BAG_PRICE_DISPLAY = '[data-role="bag_price_display"]'
  static CTA_PRICE_SELECTOR = '[data-role="cta_price"]'
  static GIFT_CARD_ERROR_SELECTOR = '#gc-error'
  /** x-product-selector renders its own error state, so it is told by event rather than attribute. */
  static SIZE_REQUIRED_EVENT = 'size-selection-required'

  /**
   * The CTA's three states, written to data-ui on the submit button. Everything visual reads them
   * from there: the three label spans via group-data-*, and the sticky rules in components.css,
   * which are gated on `visible` alone -- so ADDED is also what drops the bar back into flow.
   */
  static CTA_UNSELECTED = ''
  static CTA_SELECTED = 'visible'
  static CTA_ADDED = 'added'

  /**
   * Cart.EVENT_UPDATE_CART_COUNT in helpers/cart.js, repeated as a literal rather than imported:
   * this module deliberately has no imports, because selector.harness.mjs serves it as raw source
   * (theme.js, which is where it otherwise ships, cannot be loaded in a harness).
   */
  static CART_COUNT_EVENT = 'updateCartCount'

  constructor() {
    this.storage = window.wishlistStorage /** wishlist local storage  */
    this.apiRevision = '2024-06-15'

    this.formDataKeys = {
      ID: 'id',
      QUANTITY: 'quantity',
      UNAVAILABLE_STOCK: 'unavailable_stock',
      SIZE_UNIT: 'size_unit',
      VARIANT_PRICE: 'variant_price',
      BAG_PRICE_DISPLAY: 'gift-card-total',
      FROM: 'From',
      TO: 'To',
      EMAIL: 'Email',
      MESSAGE: 'Message'
    }

    this.messageClass = {
      [MESSAGE_TYPES.SUCCESS]: '',
      [MESSAGE_TYPES.ERROR]: ''
    }
    this.notifiedVariants = new Set()
    this.productId = undefined
    this.selectedVariant = undefined
    this.isGiftCardProduct = false
    this.init()
  }

  /**
   * init function fetches all the elements that the Product form are dependent of
   * and applies their eventlistener events.
   */
  init() {
    this.fetchChildren()
    if (!this.productId) return
    this.addEventListeners()
    /**
     * Covers the ordering where x-product-selector rendered and broadcast before this module ran.
     * Guarded on a variant already being in the form: the unselected state is what Liquid renders,
     * and deriving it here would point the wishlist button at a variant-less key on every load.
     */
    if (new FormData(this.form).get(this.formDataKeys.ID)) this.syncAddToCartState()
  }

  fetchChildren() {
    /** PRODUCT DETAILS */
    const productDetails = document.querySelector(ProductForm.PRODUCT_DETAILS_SELECTOR)
    this.productId = productDetails?.value || productDetails?.innerText

    /**
     * If the productId attribute is still null after the above
     * Do not progress any further
     */
    if (!this.productId) return

    /** FORM ATTRIBUTES */
    this.form = document.querySelector(ProductForm.FORM_SELECTOR)
    this.formSubmitButton = document.querySelector(ProductForm.FORM_SUBMIT_SELECTOR)
    this.productSelector = document.querySelector(ProductForm.PRODUCT_SELECT_SELECTOR)

    /** FORM SUBMIT BUTTON UNSELECTED PRODUCT */
    this.formSubmitButtonMessage = this.formSubmitButton.querySelector(ProductForm.FORM_SUBMIT_MESSAGE_SELECTOR)

    /**
     * Both CTA states carry a price slot, and Liquid seeds both with the product's "from" price —
     * which is what we restore to when the selection is cleared.
     */
    this.ctaPriceElements = Array.from(this.formSubmitButton.querySelectorAll(ProductForm.CTA_PRICE_SELECTOR))
    this.defaultCtaPrice = this.ctaPriceElements[0]?.textContent ?? ''

    /** KLAVIYO ATTRIBUTES */
    this.notifyForm = document.querySelector(ProductForm.NOTIFY_SELECTOR)
    this.notifyInputForm = document.querySelector(ProductForm.NOTIFY_INPUT_SELECTOR)

    this.notifySuccessElement = document.querySelector(ProductForm.NOTIFY_SUCCESS_MESSAGE_SELECTOR)
    this.notifyErrorElement = document.querySelector(ProductForm.NOTIFY_ERROR_MESSAGE_SELECTOR)

    /**
     * PRE-RELEASE (Coming Soon). The Klaviyo endpoint is variant-scoped, but a pre-release
     * shopper can notify without picking a size — hence the fallback variant from Liquid.
     */
    const preRelease = document.querySelector(ProductForm.PRE_RELEASE_SELECTOR)
    this.isPreRelease = !!preRelease
    this.fallbackVariantId = preRelease?.dataset?.fallbackVariant

    /** WISHLIST SELECTOR */
    this.wishlistButton = document.querySelector(ProductForm.WISHLIST_BUTTON_SELECTOR)
    this.checkProductType()
  }

  checkProductType() {
    const giftCard = document.querySelector(ProductForm.IS_GIFT_CARD_SELECTOR)
    if (!giftCard) return
    this.isGiftCardProduct = true
    /** One per CTA state — filling only the first left the selected (blue) CTA priceless. */
    this.bagPriceDisplays = Array.from(document.querySelectorAll(ProductForm.BAG_PRICE_DISPLAY))
  }

  addEventListeners() {
    this.form.addEventListener('click', this.handleEnableAddToCartCallback.bind(this))
    this.formSubmitButton.addEventListener('click', this.handleAddToCartCallback.bind(this))

    /**
     * Fired by Cart.getCart() after every mutation and once on load, so the CTA settles into the
     * added state for a variant already in the bag, and back out of it when the shopper removes
     * the line from the mini cart. Guarded on a variant being in the form for the same reason the
     * initial sync in init() is: with nothing selected there is no state to resolve.
     */
    document.addEventListener(ProductForm.CART_COUNT_EVENT, () => {
      if (new FormData(this.form).get(this.formDataKeys.ID)) this.syncAddToCartState()
    })

    /**
     * A ?variant= link seeds x-product-selector with no click, so the CTA has to be derivable
     * from the broadcast too. Deferred a frame because a user selection dispatches this from the
     * size pill's own handler, ahead of the form click that syncs it — by rAF that has run, and
     * the variant check below makes this a no-op for every interactive path.
     */
    window.addEventListener('variant-selected', (event) => {
      const id = String(event.detail?.id ?? '')
      requestAnimationFrame(() => {
        if (!id || id === String(this.getSelectedVariant() ?? '')) return
        this.syncAddToCartState()
      })
    })
  }

  setSelectedVariant(variant) {
    this.selectedVariant = variant
  }

  getSelectedVariant() {
    return this.selectedVariant
  }

  /**
   *
   * @param {*} formData FormData type
   */
  pullFormData(formData) {
    const keys = Object.values(this.formDataKeys)
    const payload = keys.reduce((acc, key) => ({ ...acc, [key]: formData.get(key) ?? '' }), {})
    this.setSelectedVariant(payload[this.formDataKeys.ID])
    return payload
  }

  /**
   *
   * @param {*} event onClick EventListener
   */
  handleEnableAddToCartCallback(event) {
    event.preventDefault()
    this.syncAddToCartState()
  }

  /**
   * Writes the CTA's right-hand slot. A gift card's denomination is filled by the gift-card branch
   * of syncAddToCartState into its own span, so its markup is left alone here.
   */
  setCtaPrice(value) {
    if (this.isGiftCardProduct === true) return
    this.ctaPriceElements.forEach((node) => {
      node.textContent = value
    })
  }

  /**
   * Brings the CTA, the notify form and the wishlist target in line with whatever variant the
   * form currently holds. Driven by a click on the form and by `variant-selected`, so a shared
   * or reloaded ?variant= link lands in the same state a click would have produced.
   */
  syncAddToCartState() {
    if (!this.form) return
    const formData = new FormData(this.form)
    const element = this.formSubmitButton
    const data = this.pullFormData(formData)

    /** Data attributes */
    const variantSelected = data[this.formDataKeys.ID]
    const unavailableStock = data[this.formDataKeys.UNAVAILABLE_STOCK]
    const variantPrice = data[this.formDataKeys.VARIANT_PRICE]

    this.handleWishList()
    this.resetElements()

    /**
     * Coming Soon owns its own copy ("Coming Soon | Notify Me") and keeps its email form open
     * from first paint, so none of the stock-driven relabelling below applies to it.
     */
    if (this.isPreRelease) {
      element.setAttribute('data-ui', ProductForm.CTA_UNSELECTED)
      return
    }

    /**
     * Check to see if an item is marked with unavailable stock. Both notification types read
     * "Out of Stock"; they differ in the price slot, which offers the subscription only where a
     * notify form is actually there to open.
     */
    if (unavailableStock) {
      element.setAttribute('data-ui', ProductForm.CTA_UNSELECTED)
      this.formSubmitButtonMessage.innerText = CART_MESSAGES.out_of_stock()

      const notificationType = this.formSubmitButtonMessage?.dataset?.notificationType
      if (notificationType === 'sold_out') {
        this.setCtaPrice(variantPrice || this.defaultCtaPrice)
        if (this.notifyForm) this.notifyForm.dataset.ui = ''
        this.toggleAddToCartDisabled(true)
        return
      }

      this.setCtaPrice(CTA_PRICE_MESSAGES.notify())
      if (this.notifyForm) this.notifyForm.dataset.ui = 'visible'
      this.handleVariantChange(this.getSelectedVariant())
      return
    }

    if (!variantSelected) {
      this.setCtaPrice(this.defaultCtaPrice)
      return
    }

    if (this.notifyForm) this.notifyForm.dataset.ui = ''
    this.formSubmitButtonMessage.innerText = CART_MESSAGES.select()
    this.setCtaPrice(variantPrice || this.defaultCtaPrice)
    element.setAttribute(
      'data-ui',
      this.isVariantInCart(variantSelected) ? ProductForm.CTA_ADDED : ProductForm.CTA_SELECTED
    )

    if (this.isGiftCardProduct === true) {
      const total = data[this.formDataKeys.BAG_PRICE_DISPLAY]
      this.bagPriceDisplays.forEach((node) => {
        node.innerText = total
      })
    }
  }

  /**
   * Whether the bag already holds this exact variant. cart.js reports variant_id as a number and
   * the form carries it as a string, so both sides are normalised -- a strict compare here silently
   * never matches, which looks exactly like the feature not being wired up.
   */
  isVariantInCart(variantId) {
    const items = window.Cart?.value?.items
    if (!variantId || !Array.isArray(items)) return false
    return items.some((item) => String(item.variant_id) === String(variantId))
  }

  isAdded(element) {
    return (element?.dataset?.ui ?? '').split(' ').includes(ProductForm.CTA_ADDED)
  }

  validateGiftCardForm(data) {
    if (!data[this.formDataKeys.FROM]) {
      this.displayErrorMessage('Sender Name is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    if (!data[this.formDataKeys.TO]) {
      this.displayErrorMessage('Recipient Name is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    if (!data[this.formDataKeys.EMAIL]) {
      this.displayErrorMessage('Recipient Email is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }

    if (!this.validateEmail(data[this.formDataKeys.EMAIL])) {
      this.displayErrorMessage('Recipient Email is invalid', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    return true
  }

  itemPayload(data) {
    const id = data[this.formDataKeys.ID]
    const quantity = data[this.formDataKeys.QUANTITY]
    const item = { id, quantity }

    if (this.isGiftCardProduct === true) {
      const itemWithProperties = {
        ...item,
        properties: {
          From: data[this.formDataKeys.FROM],
          To: data[this.formDataKeys.TO],
          Email: data[this.formDataKeys.EMAIL],
          Message: data[this.formDataKeys.MESSAGE]
        }
      }

      return itemWithProperties
    }

    /**
     * The unit tab the size was picked on, so the cart can show it back the same way. Underscore
     * prefixed to stay out of checkout; a line added anywhere that does not set it falls back to
     * the product's x-sizing tag (helpers/size-units.mjs).
     */
    const sizeUnit = data[this.formDataKeys.SIZE_UNIT]
    if (sizeUnit) {
      return { ...item, properties: { _size_unit: sizeUnit } }
    }

    return item
  }

  /**
   *
   * @param {*} event onClick EventListener
   */
  async handleAddToCartCallback(event) {
    event.preventDefault()
    event.stopPropagation()
    const ele = event.currentTarget
    const formData = new FormData(this.form)
    const data = this.pullFormData(formData)
    const unavailableStock = data[this.formDataKeys.UNAVAILABLE_STOCK]

    /**
     * Coming Soon subscribes rather than adds to cart, and must not demand a size first —
     * the whole product is pre-release, so any variant identifies it to Klaviyo.
     */
    if (this.isPreRelease) {
      await this.klaviyoBackInStockNotifications({
        customerEmail: this.notifyInputForm?.value ?? '',
        klaviyoCompanyId: this.notifyInputForm?.dataset?.klaviyoCompanyId,
        unavailableStockVariant: this.getSelectedVariant() || this.fallbackVariantId
      })
      return
    }

    /**
     * Already in the bag: show it rather than silently stacking a second line. The shopper's route
     * back to adding another is the mini cart's quantity control.
     */
    if (this.isAdded(ele)) {
      window.Cart?.dispatchOpenMiniCart()
      return
    }

    /** If no values have been selected scroll the product variant selector into view */
    if (!ele.dataset?.ui && !unavailableStock) {
      window.dispatchEvent(new CustomEvent(ProductForm.SIZE_REQUIRED_EVENT))
      this.productSelector.scrollIntoView()
      return
    }

    if (this.isGiftCardProduct === true && !this.validateGiftCardForm(data)) {
      return
    }

    if (unavailableStock) {
      const notificationType = this.formSubmitButtonMessage?.dataset?.notificationType
      if (notificationType === 'sold_out') {
        return
      }
      const customerEmail = this.notifyInputForm?.value
      const klaviyoCompanyId = this.notifyInputForm.dataset.klaviyoCompanyId
      const unavailableStockVariant = this.getSelectedVariant()
      await this.klaviyoBackInStockNotifications({
        customerEmail,
        klaviyoCompanyId,
        unavailableStockVariant
      })
      return
    }

    const item = this.itemPayload(data)
    window.Cart.addLineItems(item)
  }

  /**
   * Because we need to check if the item selected is in the wishlist by product variant
   * we will need to trigger a event to check the wishlistStorage whenever the Form onChange
   * is triggered.
   */
  handleWishList() {
    if (GM_STATE.customer.logged || !this.wishlistButton) return
    const variant = this.getSelectedVariant()
    const item = `${this.productId}:${variant}`
    this.wishlistButton.dataset.product = item
    this.wishlistButton.reRun()
  }

  /** Klaviyo Back in stock notifications */
  async klaviyoBackInStockNotifications({ customerEmail, klaviyoCompanyId, unavailableStockVariant }) {
    this.resetElements()
    if (!customerEmail.length) {
      this.displayErrorMessage('Email Address can not be blank.')
      return
    }
    const emailIsValid = this.validateEmail(customerEmail)
    if (!emailIsValid) {
      this.displayErrorMessage('Please enter in a valid email.')
      return
    }

    try {
      const response = await this.handleSubscription({ customerEmail, klaviyoCompanyId, unavailableStockVariant })
      await this.handleResponse(response, unavailableStockVariant)
    } catch (err) {
      console.error(err)
    }
  }

  handleVariantChange(id) {
    this.notifiedVariants.has(id) ? this.displaySuccessMessage() : this.resetElements()
  }

  validateEmail(email) {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
  }

  /**
   *

   *
   * @param {*} param0
   * @returns
   */
  async handleSubscription({ customerEmail, klaviyoCompanyId, unavailableStockVariant }) {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: customerEmail }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${unavailableStockVariant}`
            }
          }
        }
      }
    }

    return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoCompanyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: this.apiRevision
      },
      body: JSON.stringify(payload)
    })
  }

  async handleResponse(response, variantId) {
    if (response.ok) {
      this.notifiedVariants.add(variantId)
      this.displaySuccessMessage()
    } else {
      await this.handleSubscriptionError(response)
    }
  }

  async handleSubscriptionError(response) {
    try {
      const { errors } = await response.json()
      const errorMessages = {
        variant_not_found: 'Error submitting form, please try again or contact support',
        invalid: 'Invalid email'
      }

      this.displayErrorMessage(errorMessages[errors[0].code] || errorMessages.variant_not_found)
    } catch (error) {
      console.error('Error parsing response:', error)
      this.displayErrorMessage('Error submitting form, please try again or contact support')
    }
  }

  toggleAddToCartDisabled(isDisabled) {
    if (isDisabled) {
      this.formSubmitButton.setAttribute('disabled', '')
      this.formSubmitButton.classList.add('opacity-70')
      return
    }
    this.formSubmitButton.removeAttribute('disabled', '')
    this.formSubmitButton.classList.remove('opacity-70')
  }

  applyNoStockAvailableCTA() {
    this.formSubmitButtonMessage.innerText = CART_MESSAGES.out_of_stock()
  }

  displaySuccessMessage() {
    this.notifyInputForm.classList.add('hidden')
    this.applyNoStockAvailableCTA()
    this.toggleAddToCartDisabled(true)
    this.notifySuccessElement?.classList.remove('-mt-7')
    this.notifySuccessElement?.classList.remove('opacity-0')
    this.notifySuccessElement?.classList.remove('max-h-0')
    this.notifySuccessElement?.classList.add('pt-2')
  }

  displayErrorMessage(message, elementIDOverride = false) {
    if (elementIDOverride) {
      this.notifyErrorElement = document.querySelector(elementIDOverride)
    }
    if (this.notifyErrorElement) {
      this.notifySuccessElement?.classList.remove('-mt-7')
      this.notifyErrorElement.textContent = message
      this.notifyErrorElement.classList.remove('max-h-0')
    }
  }

  resetElements() {
    if (this.notifyInputForm) this.notifyInputForm.classList.remove('hidden')
    this.toggleAddToCartDisabled(false)
    if (this.notifySuccessElement) {
      this.notifySuccessElement?.classList.add('opacity-0')
      this.notifySuccessElement?.classList.add('-mt-7')
    }
    if (this.notifyErrorElement) {
      this.notifyErrorElement?.classList.add('max-h-0')
    }
  }
}

const productForm = new ProductForm()
